import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import ModuleTour from '../components/Training/ModuleTour';
import { useIsMobile } from '../hooks/useIsMobile';

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor">
    <path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38c1.45.79 3.08 1.21 4.79 1.21 5.46 0 9.91-4.45 9.91-9.91S17.5 2 12.04 2zm0 18.15c-1.48 0-2.93-.4-4.2-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.15 8.15 0 0 1-1.25-4.38c0-4.54 3.7-8.24 8.24-8.24 4.54 0 8.24 3.7 8.24 8.24 0 4.54-3.7 8.24-8.24 8.24zm4.52-6.16c-.25-.12-1.47-.72-1.7-.8-.23-.08-.4-.12-.57.12-.17.25-.65.8-.8.97-.15.17-.29.19-.54.06-.25-.12-1.05-.39-2-1.23-.74-.66-1.24-1.47-1.39-1.72-.14-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.12-.15.16-.25.25-.42.08-.17.04-.31-.02-.43-.06-.12-.57-1.38-.78-1.89-.2-.49-.41-.42-.57-.43-.15-.01-.31-.01-.48-.01-.17 0-.43.06-.66.31-.23.25-.87.85-.87 2.08 0 1.22.89 2.4 1.02 2.57.12.17 1.75 2.67 4.24 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.55.1.47-.07 1.47-.6 1.68-1.18.21-.58.21-1.08.15-1.18-.06-.1-.23-.17-.48-.29z" />
  </svg>
);

const InstagramIcon = () => (
  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="18" height="18" rx="5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
  </svg>
);

const MessengerIcon = () => (
  <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.15 2 11.25c0 2.9 1.44 5.49 3.69 7.19V22l3.37-1.85c.9.25 1.85.38 2.94.38 5.52 0 10-4.15 10-9.28C22 6.15 17.52 2 12 2zm1.01 12.5-2.55-2.72-4.98 2.72 5.48-5.82 2.6 2.72 4.93-2.72-5.48 5.82z" />
  </svg>
);

const THREAD_PAGE_SIZE = 10;
const CONTACTS_PAGE_SIZE = 10;

// Mesajların sadece saati (HH:mm) gösteriliyordu - hangi gün geldiği belli
// değildi. Liste: bugün "14:32", dün "Dün 14:32", bu yıl "18 Eki 14:32",
// önceki yıllar "18 Eki 2025". Sohbet detayında da gün değişince ayraç çıkar.
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const dayDiffFromToday = (d) => Math.round((startOfDay(new Date()) - startOfDay(d)) / 86400000);

const formatListTimestamp = (value) => {
  const d = new Date(value);
  if (isNaN(d)) return '';
  const diff = dayDiffFromToday(d);
  if (diff === 0) return format(d, 'HH:mm');
  if (diff === 1) return `Dün ${format(d, 'HH:mm')}`;
  if (d.getFullYear() === new Date().getFullYear()) return format(d, 'd MMM HH:mm', { locale: tr });
  return format(d, 'd MMM yyyy', { locale: tr });
};

const formatDayDivider = (value) => {
  const d = new Date(value);
  if (isNaN(d)) return '';
  const diff = dayDiffFromToday(d);
  if (diff === 0) return 'Bugün';
  if (diff === 1) return 'Dün';
  return format(d, 'd MMMM yyyy, EEEE', { locale: tr });
};

const isSameDay = (a, b) => {
  const da = new Date(a), db = new Date(b);
  return da.getFullYear() === db.getFullYear() && da.getMonth() === db.getMonth() && da.getDate() === db.getDate();
};

// WhatsApp veya telefon numarasını okunabilir Türkiye / uluslararası formata çevirir
const formatDisplayPhone = (p) => {
  if (!p) return '';
  const str = String(p).trim();
  const digits = str.replace(/\D/g, '');
  if (digits.length === 12 && digits.startsWith('90')) {
    return `+90 ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8, 10)} ${digits.slice(10, 12)}`;
  }
  if (digits.length === 10 && digits.startsWith('5')) {
    return `0${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8, 10)}`;
  }
  if (digits.length === 11 && digits.startsWith('05')) {
    return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 9)} ${digits.slice(9, 11)}`;
  }
  return str.startsWith('+') ? str : (digits.length >= 10 && digits.length <= 13 ? `+${digits}` : str);
};

function Messages() {
  // Sol paneldeki kişi listesi - SAYFALI (varsayılan ilk 10 kişi), kişi başına SADECE
  // son mesaj + okunmamış sayısı (bkz. GET /api/messages/conversations). Açık sohbetin
  // TAM geçmişi burada değil, ayrı ve sayfalı olarak threadMessages'ta tutuluyor -
  // eskiden şubenin TÜM kişileri tek seferde (ve 4 saniyede bir) çekiliyordu, bu da
  // sayfayı ağırlaştırıyordu (gerçek veride 4-5 saniye/280KB'a çıktığı ölçüldü).
  const isMobile = useIsMobile();
  const [mobileView, setMobileView] = useState('list'); // 'list' | 'chat'
  const [contactsSummary, setContactsSummary] = useState([]);
  const [contactsHasMore, setContactsHasMore] = useState(false);
  const [contactsLoadingMore, setContactsLoadingMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activePlatform, setActivePlatform] = useState('all');
  const [selectedContactId, setSelectedContactId] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesScrollRef = useRef(null);
  const [typedMessage, setTypedMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [tagsMap, setTagsMap] = useState({});
  const [contactSearch, setContactSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [allCategories, setAllCategories] = useState([]);
  const [restrictAiForCustomers, setRestrictAiForCustomers] = useState(false);
  const [savingRestriction, setSavingRestriction] = useState(false);
  const [creatingAppointment, setCreatingAppointment] = useState(false);

  // Açık sohbetin sayfalı mesaj geçmişi (eskiden-yeniye sıralı).
  const [threadMessages, setThreadMessages] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadLoadingOlder, setThreadLoadingOlder] = useState(false);
  const [threadHasMore, setThreadHasMore] = useState(false);
  const preserveScrollRef = useRef(null);
  const navigate = useNavigate();

  // Ayarlar > Genel'de açılıp kapatılabilen bir özellik (bkz. Settings.js
  // "Mesajlarda Ön Görüşme İşlemleri") - açıksa, sohbete karşılık gelen bir ön
  // görüşme talebi bulunduğunda WhatsApp sohbetinin en üstünde Ön Görüşme'deki AYNI
  // işlem butonları (Arandı/Cevap Yok/Tekrar Ara/Randevulandı/Tamamlandı/Kayıp/Sil) görünür.
  const [preconsToggleOn, setPreconsToggleOn] = useState(false);
  const [linkedPrecons, setLinkedPrecons] = useState(null);
  const [loadingLinkedPrecons, setLoadingLinkedPrecons] = useState(false);
  const [showMsgCallbackPicker, setShowMsgCallbackPicker] = useState(false);
  const [msgCallbackDateInput, setMsgCallbackDateInput] = useState('');
  const [savingMsgCallback, setSavingMsgCallback] = useState(false);
  const [schedulingFromMsg, setSchedulingFromMsg] = useState(false);

  const fetchAiRestriction = async () => {
    try {
      const token = localStorage.getItem('crm_token');
      const res = await axios.get('/api/ai/settings', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRestrictAiForCustomers(!!res.data?.restrict_ai_for_customers);
    } catch (err) {
      console.error('Fetch AI restriction error:', err);
    }
  };

  // Ayarlar > Genel'deki anahtar branches.notification_settings JSON'ında saklanıyor
  // (Settings.js'teki mevcut bildirim ayarlarıyla AYNI kolon/deseni yeniden kullanıyor,
  // yeni bir DB kolonu gerekmiyor).
  const fetchPreconsToggle = async () => {
    try {
      const token = localStorage.getItem('crm_token');
      const res = await axios.get('/api/branches', { headers: { Authorization: `Bearer ${token}` } });
      const raw = res.data?.[0]?.notification_settings;
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : (raw || {});
      setPreconsToggleOn(!!parsed.show_precons_in_messages);
    } catch (err) {
      console.error('Fetch precons toggle error:', err);
    }
  };

  const toggleRestrictAiForCustomers = async () => {
    const next = !restrictAiForCustomers;
    setSavingRestriction(true);
    try {
      const token = localStorage.getItem('crm_token');
      await axios.put('/api/ai/settings', { restrict_ai_for_customers: next }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRestrictAiForCustomers(next);
      toast.success(next ? '✅ AI artık müşteri listesindeki kişilere yazmayacak' : '✅ AI artık herkese normal şekilde cevap verecek');
    } catch (err) {
      console.error('Toggle AI restriction error:', err);
      toast.error('❌ Kaydedilemedi, tekrar deneyin');
    }
    setSavingRestriction(false);
  };

  useEffect(() => {
    fetchAiRestriction();
    fetchCategories();
    fetchPreconsToggle();
  }, []);

  useEffect(() => {
    fetchConversationsInitial();
    fetchTags();
  }, [activePlatform]);

  // Sadece EN ÜST sayfayı (en son 10 kişi) tazeler ve mevcut listeye BİRLEŞTİRİR -
  // "Daha Fazla Yükle" ile açılmış ek sayfaları silmez/sıfırlamaz, sadece güncel tutar.
  useEffect(() => {
    const interval = setInterval(() => {
      pollConversationsTop();
      fetchTags();
    }, 4000);
    return () => clearInterval(interval);
  }, [activePlatform]);

  const handleSendMessage = async (e) => {
    if (e) e.preventDefault();
    if (!typedMessage.trim() || sending || !selectedContact) return;

    setSending(true);
    try {
      const token = localStorage.getItem('crm_token');
      const response = await axios.post('/api/messages/send', {
        phone: selectedContact.phone,
        platform: selectedContact.platform,
        content: typedMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setTypedMessage('');
        const newMsg = response.data.message;
        forceScrollRef.current = true;
        setThreadMessages(prev => [...prev, newMsg]);
        // Sol panel bir sonraki 4sn'lik yoklamaya kadar bayat görünmesin diye
        // son mesaj önizlemesini burada da hemen güncelliyoruz.
        setContactsSummary(prev => prev.map(c =>
          (c.phone === newMsg.phone && c.platform === newMsg.platform)
            ? { ...c, content: newMsg.content, direction: 'outgoing', created_at: newMsg.created_at }
            : c
        ));
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Mesaj gönderilemedi.');
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  const handleToggleMode = async (phone, mode, platform) => {
    try {
      const token = localStorage.getItem('crm_token');
      const response = await axios.post('/api/messages/session/toggle', {
        phone,
        mode,
        platform
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setContactsSummary(prev => prev.map(c =>
          (c.phone === phone && c.platform === platform) ? { ...c, handover_mode: mode } : c
        ));
      }
    } catch (err) {
      console.error(err);
    }
  };

  // WhatsApp'ta numara WAHA/LID iç kimliği olabilir (bkz. realPhone alanı) - randevu
  // için her zaman aranabilir GERÇEK numara lazım. Aynı arama+eşleme deseni
  // PreConsultations.js'teki resolveOrCreateCustomer'dan alındı (LIKE bulanık arama
  // sonuçları arasından son 10 hanesi birebir eşleşen TEK kaydı seçmek, yanlışlıkla
  // başka bir müşteriye bağlanmamak için).
  const normalizePhoneDigits = (p) => String(p || '').replace(/\D/g, '').slice(-10);

  // PreConsultations.js'teki AYNI datetime-local yardımcıları - callback_date artık
  // DATETIME (saat de taşıyor), bkz. o dosyadaki yorum için db/connection.js'in
  // DATETIME/TIMESTAMP typeCast'i notu.
  const toDatetimeLocalValue = (d) => {
    const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0'), day = String(d.getDate()).padStart(2, '0');
    const h = String(d.getHours()).padStart(2, '0'), min = String(d.getMinutes()).padStart(2, '0');
    return `${y}-${m}-${day}T${h}:${min}`;
  };
  const toDatetimeLocalInput = (mysqlDatetimeStr) => mysqlDatetimeStr ? String(mysqlDatetimeStr).replace(' ', 'T').slice(0, 16) : '';
  const toMySQLDatetime = (datetimeLocalValue) => datetimeLocalValue ? datetimeLocalValue.replace('T', ' ') + ':00' : null;

  // PreConsultations.js'teki getStatusBadge ile AYNI renk haritası - iki sayfada da
  // aynı durum aynı renkle görünsün diye.
  const getStatusBadgeInline = (status) => {
    const badges = {
      pending: { bg: 'rgba(245, 158, 11, 0.15)', color: '#fbbf24', text: 'Bekliyor' },
      contacted: { bg: 'rgba(59, 130, 246, 0.15)', color: '#60a5fa', text: 'Arandı' },
      no_response: { bg: 'rgba(107, 114, 128, 0.15)', color: '#9ca3af', text: 'Cevap Yok' },
      callback_later: { bg: 'rgba(249, 115, 22, 0.15)', color: '#fb923c', text: 'Tekrar Aranacak' },
      scheduled: { bg: 'rgba(16, 185, 129, 0.15)', color: '#34d399', text: 'Randevulu' },
      completed: { bg: 'rgba(99, 102, 241, 0.15)', color: '#a78bfa', text: 'Tamamlandı' },
      lost: { bg: 'rgba(239, 68, 68, 0.15)', color: '#f87171', text: 'Kayıp' }
    };
    const b = badges[status] || { bg: 'rgba(255,255,255,0.06)', color: 'var(--text-muted)', text: status };
    return <span style={{ background: b.bg, color: b.color, padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' }}>{b.text}</span>;
  };

  const handleCreateAppointment = async () => {
    const dialPhone = selectedContact.realPhone || selectedContact.phone;
    if (!dialPhone) return;

    setCreatingAppointment(true);
    try {
      const token = localStorage.getItem('crm_token');
      const headers = { Authorization: `Bearer ${token}` };
      let customerId = null;
      let callerName = '';

      try {
        const res = await axios.get(`/api/customers?search=${encodeURIComponent(dialPhone)}&limit=20`, { headers });
        const targetDigits = normalizePhoneDigits(dialPhone);
        const match = (res.data?.data || []).find(c => normalizePhoneDigits(c.phone) === targetDigits);
        if (match) {
          customerId = match.id;
          callerName = `${match.first_name || ''} ${match.last_name || ''}`.trim();
        }
      } catch (e) { /* arama başarısız olursa telefon numarasıyla boş isimle devam edilir */ }

      // Eşleşme yoksa, sohbette görünen isim gerçek bir isimse (sadece numaranın
      // kendisi değilse) öneri olarak kullanılır - Takvim'deki "Yeni Müşteri" alt
      // formunu elle doldurmaktan kurtarır.
      if (!callerName && selectedContact.name && selectedContact.name !== dialPhone && selectedContact.name !== selectedContact.phone) {
        callerName = selectedContact.name;
      }

      navigate('/calendar', {
        state: {
          preConsultation: {
            customer_id: customerId,
            caller_name: callerName,
            phone: dialPhone,
            preferred_date: null,
            notes: ''
          }
        }
      });
    } finally {
      setCreatingAppointment(false);
    }
  };

  // Ayarlar'dan açıksa: seçili WhatsApp sohbetine karşılık gelen (varsa) ön görüşme
  // talebini bulur - /api/pre-consultations'ın search'ü bulanık (LIKE) eşleştirdiği
  // için sonuçlar arasından son 10 hanesi BİREBİR eşleşen TEK kaydı seçiyoruz (aynı
  // desen handleCreateAppointment/PreConsultations.js'teki resolveOrCreateCustomer'da
  // da kullanılıyor - yanlışlıkla başka bir kişinin talebine bağlanmamak için).
  useEffect(() => {
    setLinkedPrecons(null);
    setShowMsgCallbackPicker(false);
    if (!preconsToggleOn || !selectedContact || selectedContact.platform !== 'whatsapp') return;
    const dialPhone = selectedContact.realPhone || selectedContact.phone;
    if (!dialPhone) return;

    let cancelled = false;
    setLoadingLinkedPrecons(true);
    (async () => {
      try {
        const token = localStorage.getItem('crm_token');
        const res = await axios.get(`/api/pre-consultations?search=${encodeURIComponent(dialPhone)}&limit=20`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const targetDigits = normalizePhoneDigits(dialPhone);
        const candidates = (res.data?.requests || []).filter(r => normalizePhoneDigits(r.phone) === targetDigits);
        // En son oluşturulan (zaten created_at DESC sıralı geliyor) eşleşen talep.
        if (!cancelled) setLinkedPrecons(candidates[0] || null);
      } catch (err) {
        console.error(err);
      } finally {
        if (!cancelled) setLoadingLinkedPrecons(false);
      }
    })();

    return () => { cancelled = true; };
  }, [selectedContactId, preconsToggleOn]);

  useEffect(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(10, 0, 0, 0);
    setMsgCallbackDateInput(toDatetimeLocalInput(linkedPrecons?.callback_date) || toDatetimeLocalValue(tomorrow));
  }, [linkedPrecons?.id]);

  // Sohbete karşılık gelen bir ön görüşme talebi YOKSA, herhangi bir işlem butonuna
  // basıldığında (Sil hariç) bir tane oluşturulur - "arandı/cevap yok gibi
  // işaretlemeler yapıldığında ön görüşme ekranına düşsün" (kullanıcı talebi).
  // POST /api/pre-consultations'ın kendi dedup'ı var (aynı telefon için açık bir talep
  // zaten varsa onu döndürür) - burada TEKRAR oluşturma riski yok.
  const ensureLinkedPrecons = async () => {
    if (linkedPrecons) return linkedPrecons;
    const dialPhone = selectedContact.realPhone || selectedContact.phone;
    const token = localStorage.getItem('crm_token');
    const callerName = (selectedContact.name && selectedContact.name !== dialPhone && selectedContact.name !== selectedContact.phone)
      ? selectedContact.name : '';
    const res = await axios.post('/api/pre-consultations', {
      phone: dialPhone, caller_name: callerName, country_code: 'TR', source: 'whatsapp'
    }, { headers: { Authorization: `Bearer ${token}` } });
    const created = {
      id: res.data.id, phone: dialPhone, caller_name: callerName, status: 'pending',
      source: 'whatsapp', customer_id: null, notes: '', preferred_date: null,
      callback_date: null, ai_summary: null
    };
    setLinkedPrecons(created);
    return created;
  };

  const updateLinkedPreconsStatus = async (newStatus) => {
    try {
      const token = localStorage.getItem('crm_token');
      const target = await ensureLinkedPrecons();
      await axios.put(`/api/pre-consultations/${target.id}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLinkedPrecons({ ...target, status: newStatus });
    } catch (err) {
      console.error(err);
      alert('Güncellenemedi.');
    }
  };

  const saveMsgCallbackLater = async () => {
    if (!msgCallbackDateInput) return;
    setSavingMsgCallback(true);
    try {
      const token = localStorage.getItem('crm_token');
      const target = await ensureLinkedPrecons();
      await axios.put(`/api/pre-consultations/${target.id}`,
        { status: 'callback_later', callback_date: toMySQLDatetime(msgCallbackDateInput) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Yerel state'i backend'in döneceği AYNI biçimde ("YYYY-MM-DDTHH:mm:ss") tutuyoruz
      // ki rozet/gösterim mantığı yeniden çekmeden de doğru çalışsın.
      setLinkedPrecons({ ...target, status: 'callback_later', callback_date: `${msgCallbackDateInput}:00` });
      setShowMsgCallbackPicker(false);
    } catch (err) {
      alert(err.response?.data?.error || 'Kaydedilemedi.');
      console.error(err);
    } finally {
      setSavingMsgCallback(false);
    }
  };

  // "Randevulandı": PreConsultations.js'teki goToCalendar ile AYNI davranış - müşteri
  // yoksa otomatik oluşturulur (Randevu Oluştur butonundan farklı olarak burada bilerek
  // oluşturuluyor, çünkü nitelikli bir lead'i randevuya çevirmek zaten müşteri kaydı
  // gerektirir), ve preConsultation.id gönderildiği için Takvim kaydedince talebi
  // otomatik 'scheduled' işaretler (Calendar.js'te zaten var olan mantık).
  const goToCalendarFromMessages = async () => {
    setSchedulingFromMsg(true);
    const precons = await ensureLinkedPrecons();
    let customerId = precons.customer_id || null;
    if (!customerId) {
      try {
        const token = localStorage.getItem('crm_token');
        const headers = { Authorization: `Bearer ${token}` };
        const dialPhone = precons.phone || selectedContact.realPhone || selectedContact.phone;
        const res = await axios.get(`/api/customers?search=${encodeURIComponent(dialPhone)}&limit=20`, { headers });
        const targetDigits = normalizePhoneDigits(dialPhone);
        const match = (res.data?.data || []).find(c => normalizePhoneDigits(c.phone) === targetDigits);
        if (match) {
          customerId = match.id;
        } else {
          const parts = (precons.caller_name || '').trim().split(/\s+/).filter(Boolean);
          const first_name = parts.length > 1 ? parts.slice(0, -1).join(' ') : (parts[0] || 'Ön Görüşme Müşterisi');
          const last_name = parts.length > 1 ? parts[parts.length - 1] : '';
          const createRes = await axios.post('/api/customers', { first_name, last_name, phone: dialPhone, source: 'whatsapp' }, { headers });
          customerId = createRes.data?.id || null;
        }
      } catch (e) {
        console.error('Customer resolve/create error:', e);
        alert('Müşteri otomatik oluşturulamadı, randevu ekranında elle seçin/ekleyin.');
      }
    }
    setSchedulingFromMsg(false);
    navigate('/calendar', {
      state: {
        preConsultation: {
          id: precons.id,
          customer_id: customerId,
          caller_name: precons.caller_name || '',
          phone: precons.phone || '',
          preferred_date: precons.preferred_date || null,
          notes: precons.notes || precons.ai_summary || ''
        }
      }
    });
  };

  // İlk açılış / platform sekmesi değişimi: listeyi BAŞTAN kurar (en son 10 kişi).
  const fetchConversationsInitial = async () => {
    try {
      const token = localStorage.getItem('crm_token');
      const response = await axios.get(`/api/messages/conversations?platform=${activePlatform}&limit=${CONTACTS_PAGE_SIZE}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContactsSummary(response.data?.conversations || []);
      setContactsHasMore(!!response.data?.has_more);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // "Daha Fazla Yükle" (buton ya da listenin altına kaydırma): en eski yüklü kişiden
  // öncesini (cursor: en küçük id) getirip listenin SONUNA ekler.
  const fetchConversationsMore = async () => {
    if (contactsLoadingMore || !contactsHasMore || contactsSummary.length === 0) return;
    setContactsLoadingMore(true);
    try {
      const token = localStorage.getItem('crm_token');
      const cursor = Math.min(...contactsSummary.map(c => c.id));
      const response = await axios.get(`/api/messages/conversations?platform=${activePlatform}&limit=${CONTACTS_PAGE_SIZE}&before=${cursor}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const page = response.data?.conversations || [];
      setContactsSummary(prev => {
        const known = new Set(prev.map(c => `${c.phone}_${c.platform}`));
        return [...prev, ...page.filter(c => !known.has(`${c.phone}_${c.platform}`))];
      });
      setContactsHasMore(!!response.data?.has_more);
    } catch (err) {
      console.error(err);
    } finally {
      setContactsLoadingMore(false);
    }
  };

  // 4sn'lik yoklama: sadece en son sayfayı tekrar çeker, mevcut (varsa "Daha Fazla
  // Yükle" ile açılmış ek sayfalar dahil) listeye BİRLEŞTİRİR - contactsHasMore'a
  // dokunmaz, listeyi sıfırlamaz.
  const pollConversationsTop = async () => {
    try {
      const token = localStorage.getItem('crm_token');
      const response = await axios.get(`/api/messages/conversations?platform=${activePlatform}&limit=${CONTACTS_PAGE_SIZE}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const fresh = response.data?.conversations || [];
      if (!fresh.length) return;
      setContactsSummary(prev => {
        const byKey = new Map(prev.map(c => [`${c.phone}_${c.platform}`, c]));
        fresh.forEach(c => byKey.set(`${c.phone}_${c.platform}`, c));
        return Array.from(byKey.values());
      });
    } catch (err) { /* sessiz - bir sonraki yoklamada tekrar denenir */ }
  };

  // initial=true: sohbet ilk açılırken (ya da kişi değiştiğinde) en yeni sayfayı
  // getirir ve state'i BAŞTAN kurar. initial=false: "yukarı kaydırdıkça daha fazla
  // yükle" - mevcut en eski mesajdan (before_id) öncesini getirip BAŞA ekler.
  const fetchThread = async (contact, { initial }) => {
    if (!contact) return;
    if (initial) setThreadLoading(true); else setThreadLoadingOlder(true);
    try {
      const token = localStorage.getItem('crm_token');
      const params = new URLSearchParams({ platform: contact.platform, limit: THREAD_PAGE_SIZE });
      if (contact.phone) params.set('phone', contact.phone);
      if (!initial && threadMessages.length > 0) params.set('before_id', threadMessages[0].id);

      const res = await axios.get(`/api/messages/thread?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const page = Array.isArray(res.data) ? res.data : [];

      if (initial) {
        setThreadMessages(page);
      } else {
        // Yukarı kaydırırken görünüm sabit kalsın diye (yeni içerik BAŞA eklenince
        // tarayıcı scroll'u otomatik yukarı kaydırır) - eski scrollHeight'ı burada
        // kaydedip DOM güncellendikten sonra fark kadar telafi ediyoruz.
        const container = messagesScrollRef.current;
        preserveScrollRef.current = container ? { prevHeight: container.scrollHeight, prevTop: container.scrollTop } : null;
        setThreadMessages(prev => [...page, ...prev]);
      }
      setThreadHasMore(page.length === THREAD_PAGE_SIZE);
    } catch (err) {
      console.error(err);
    } finally {
      if (initial) setThreadLoading(false); else setThreadLoadingOlder(false);
    }
  };

  // Açık sohbette yeni mesaj var mı diye hafifçe yoklar - en yeni sayfayı tekrar
  // çekip id'ye göre birleştirir (zaten yüklü eski sayfalara dokunmadan).
  const pollThreadForNew = async (contact) => {
    if (!contact) return;
    try {
      const token = localStorage.getItem('crm_token');
      const params = new URLSearchParams({ platform: contact.platform, limit: 20 });
      if (contact.phone) params.set('phone', contact.phone);
      const res = await axios.get(`/api/messages/thread?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const latest = Array.isArray(res.data) ? res.data : [];
      if (!latest.length) return;
      setThreadMessages(prev => {
        const known = new Set(prev.map(m => m.id));
        const fresh = latest.filter(m => !known.has(m.id));
        if (!fresh.length) return prev;
        return [...prev, ...fresh].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      });
    } catch (err) { /* sessiz - bir sonraki yoklamada tekrar denenir */ }
  };

  const fetchTags = async () => {
    try {
      const token = localStorage.getItem('crm_token');
      const response = await axios.get('/api/messages/tags', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTagsMap(response.data || {});
    } catch (err) {
      console.error(err);
    }
  };

  // Sabit kategori taksonomisi - dropdown seçenekleri için (aynı liste
  // Automations.js'teki "Etikete Göre Takip" kural formunda da kullanılıyor).
  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('crm_token');
      const response = await axios.get('/api/automations/lead-categories', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllCategories(response.data?.all || []);
    } catch (err) {
      console.error(err);
    }
  };

  const markContactAsRead = async (contact) => {
    try {
      const token = localStorage.getItem('crm_token');
      // Tek istekte TÜMÜNÜ okundu işaretler (bkz. POST /mark-read) - sayfalı yükleme
      // yüzünden ekranda görünmeyen daha eski okunmamışlar da (id'leri elimizde
      // olmasa bile) bu sayede kaçırılmaz.
      await axios.post('/api/messages/mark-read', { phone: contact.phone, platform: contact.platform }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setThreadMessages(prev => prev.map(m => (m.direction === 'incoming' ? { ...m, is_read: 1 } : m)));
      setContactsSummary(prev => prev.map(c =>
        (c.phone === contact.phone && c.platform === contact.platform) ? { ...c, unread_count: 0 } : c
      ));
    } catch (error) {
      console.error(error);
    }
  };

  // Sol panel: kişi listesi doğrudan hafif özet uç noktasından (bkz. fetchConversations) -
  // artık kişi başına TÜM mesajları toplayıp gruplamaya gerek yok, backend zaten
  // kişi başına tek satır (son mesaj + gerçek COUNT() ile okunmamış sayısı) döndürüyor.
  const groupedContacts = useMemo(() => {
    return contactsSummary.map(row => {
      const key = row.phone || `unknown_${row.platform}`;
      const tagEntry = tagsMap[`${row.phone}_${row.platform}`];

      // Gerçek WhatsApp numarası / telefon
      const actualPhone = row.real_phone || row.phone;
      const formattedActualPhone = formatDisplayPhone(actualPhone);

      // İsim kontrolü: Eğer gerçek bir ad soyad varsa onu kullan,
      // 'WhatsApp Müşterisi', 'AI', 'Sistem / Personel' veya telefon/ID ise doğrudan telefon numarasını göster.
      const hasRealName = row.customer_name 
        && row.customer_name !== 'AI' 
        && row.customer_name !== 'WhatsApp Müşterisi'
        && row.customer_name !== 'Sistem / Personel'
        && row.customer_name !== row.phone
        && row.customer_name !== row.real_phone;

      const displayName = hasRealName ? row.customer_name : (formattedActualPhone || actualPhone || 'Bilinmeyen Numara');

      return {
        id: key,
        name: displayName,
        hasRealName,
        phone: row.phone,
        realPhone: row.real_phone || null,
        displayPhone: formattedActualPhone,
        platform: row.platform,
        accountLabel: row.account_label || null,
        lastMessage: { content: row.content, direction: row.direction, created_at: row.created_at },
        unreadCount: parseInt(row.unread_count, 10) || 0,
        handoverMode: row.handover_mode || 'ai',
        tags: tagEntry?.tags || [],
        category: tagEntry?.category || null
      };
    }).sort((a, b) => new Date(b.lastMessage.created_at) - new Date(a.lastMessage.created_at));
  }, [contactsSummary, tagsMap]);

  const filteredContacts = useMemo(() => {
    const q = contactSearch.trim().toLocaleLowerCase('tr');
    return groupedContacts.filter(c => {
      if (categoryFilter && c.category !== categoryFilter) return false;
      if (!q) return true;
      return (c.name || '').toLocaleLowerCase('tr').includes(q) ||
        (c.displayPhone || '').includes(q) ||
        (c.phone || '').includes(q) ||
        (c.realPhone || '').includes(q);
    });
  }, [groupedContacts, contactSearch, categoryFilter]);

  // Kişi bilgisi (isim, etiket, handover modu vb.) özet listesinden, mesaj
  // GÖVDESİ ise ayrı sayfalı state'ten (threadMessages) geliyor - ikisi burada
  // birleşiyor ki JSX eskisi gibi tek bir selectedContact.messages okuyabilsin.
  const selectedContactBase = useMemo(() => {
    if (!selectedContactId) return null;
    return groupedContacts.find(c => c.id === selectedContactId) || null;
  }, [selectedContactId, groupedContacts]);

  const selectedContact = useMemo(() => {
    if (!selectedContactBase) return null;
    return { ...selectedContactBase, messages: threadMessages };
  }, [selectedContactBase, threadMessages]);

  // Eğitim turu bu sayfaya "Öğren" ile geldiğinde, AI/Personel devir butonlarını
  // gösterebilmek için (bunlar sadece bir sohbet seçiliyken render oluyor) varsa ilk
  // sohbeti otomatik seçiyoruz.
  const location = useLocation();
  useEffect(() => {
    if (location.state?.startTour === 'messages' && !selectedContactId && groupedContacts.length > 0) {
      setSelectedContactId(groupedContacts[0].id);
    }
    // eslint-disable-next-line
  }, [location.state, groupedContacts]);

  useEffect(() => {
    if (selectedContact && selectedContact.unreadCount > 0) {
      markContactAsRead(selectedContact);
    }
  }, [selectedContact]);

  // Kişi değişince sayfalı geçmişi sıfırdan yükler. forceScrollRef, bir sonraki
  // threadMessages güncellemesinde en alta (smooth değil, anlık) kaydırılmasını işaretler.
  const forceScrollRef = useRef(false);
  useEffect(() => {
    forceScrollRef.current = true;
    setThreadMessages([]);
    setThreadHasMore(false);
    if (selectedContactBase) fetchThread(selectedContactBase, { initial: true });
    // eslint-disable-next-line
  }, [selectedContactId]);

  // Açık sohbette yeni mesaj var mı diye hafifçe yoklar (eskiden tüm şubeyi çeken
  // 4sn'lik döngünün yerini alıyor - artık sadece AÇIK olan tek sohbet için).
  useEffect(() => {
    if (!selectedContactId) return;
    const interval = setInterval(() => {
      if (selectedContactBase) pollThreadForNew(selectedContactBase);
    }, 4000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [selectedContactId]);

  // Scroll davranışı: eski mesaj başa eklendiyse (yukarı kaydırma) görünümü sabit
  // tut; kişi yeni seçildiyse ya da az önce mesaj gönderildiyse en alta kaydır;
  // aksi halde (yoklamadan gelen yeni mesaj) sadece kullanıcı zaten en alta
  // yakınsa kaydır - geçmişi okurken rahatsız etmesin.
  useEffect(() => {
    const container = messagesScrollRef.current;
    if (!container) return;

    if (preserveScrollRef.current) {
      const { prevHeight, prevTop } = preserveScrollRef.current;
      container.scrollTop = container.scrollHeight - prevHeight + prevTop;
      preserveScrollRef.current = null;
      return;
    }

    if (forceScrollRef.current) {
      forceScrollRef.current = false;
      if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
      return;
    }

    const nearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 200;
    if (nearBottom && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [threadMessages]);

  const handleThreadScroll = () => {
    const container = messagesScrollRef.current;
    if (!container || threadLoadingOlder || !threadHasMore || !selectedContactBase) return;
    if (container.scrollTop < 80) {
      fetchThread(selectedContactBase, { initial: false });
    }
  };

  const platforms = [
    { id: 'all', label: 'Tümü' },
    { id: 'whatsapp', label: 'WhatsApp', color: '#25D366', icon: <WhatsAppIcon /> },
    { id: 'instagram', label: 'Instagram', color: '#E1306C', icon: <InstagramIcon /> },
    { id: 'messenger', label: 'Messenger', color: '#0084FF', icon: <MessengerIcon /> }
  ];

  const platformInfo = (platform) => platforms.find(p => p.id === platform) || { label: platform, color: '#6366f1', icon: <WhatsAppIcon /> };

  if (loading) return <div className="loading-container"><div className="loading-spinner"></div></div>;

  return (
    <div style={{ padding: '0 0 20px', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <ModuleTour moduleKey="messages" />
      <div data-tour="msg-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h1 className="page-title" style={{ margin: 0 }}>Sohbetler</h1>
          </div>
          <p className="page-subtitle" style={{ margin: 0, marginTop: '4px' }}>Tüm müşteri mesajlaşmalarını buradan takip edin.</p>
          <label
            title="Açıkken, AI zaten müşteri listenizde kayıtlı kişilere otomatik cevap vermez — sadece henüz müşteri olmayan yeni kişilere (lead) yazar."
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginTop: '10px', cursor: savingRestriction ? 'default' : 'pointer', opacity: savingRestriction ? 0.6 : 1 }}
          >
            <span className="msg-switch">
              <input
                type="checkbox"
                checked={restrictAiForCustomers}
                disabled={savingRestriction}
                onChange={toggleRestrictAiForCustomers}
              />
              <span className="msg-switch-slider" />
            </span>
            <span style={{ fontSize: '12px', fontWeight: 600, color: restrictAiForCustomers ? '#FBBF24' : 'var(--text-muted)' }}>
              🔒 Müşteri listesindekilere AI yazmasın
            </span>
          </label>
        </div>
        <div data-tour="msg-platform-filter" style={{ display: 'flex', gap: '8px', overflowX: 'auto', flexWrap: 'nowrap', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' }}>
          {platforms.map(p => (
            <button
              key={p.id}
              onClick={() => { setActivePlatform(p.id); setSelectedContactId(null); }}
              style={{
                padding: '8px 16px',
                borderRadius: '20px',
                border: '1px solid',
                borderColor: activePlatform === p.id ? (p.color || '#6366f1') : 'rgba(255,255,255,0.1)',
                background: activePlatform === p.id ? `${p.color || '#6366f1'}20` : 'rgba(255,255,255,0.02)',
                color: activePlatform === p.id ? (p.color || '#6366f1') : 'var(--text-muted)',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              {p.icon}{p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="chat-container" style={{ 
        display: 'flex', 
        height: 'calc(100vh - 170px)', 
        minHeight: isMobile ? '100dvh' : '500px',
        background: 'rgba(13, 18, 30, 0.45)',
        border: isMobile ? 'none' : '1px solid rgba(255,255,255,0.05)', 
        borderRadius: isMobile ? '0' : '16px', 
        overflow: 'hidden',
        flexDirection: isMobile ? 'column' : 'row'
      }}>
        {/* Left Sidebar - Contacts List */}
        <div data-tour="msg-contact-list" style={{
          width: isMobile ? '100%' : '340px',
          borderRight: isMobile ? 'none' : '1px solid rgba(255,255,255,0.05)',
          borderBottom: isMobile && mobileView === 'list' ? '1px solid rgba(255,255,255,0.05)' : 'none',
          display: isMobile && mobileView === 'chat' ? 'none' : 'flex',
          flexDirection: 'column',
          background: 'rgba(0,0,0,0.2)'
        }}>
          <div style={{ padding: '16px', borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <input
              type="text"
              className="input"
              placeholder="Kişilerde ara..."
              value={contactSearch}
              onChange={(e) => setContactSearch(e.target.value)}
              style={{ width: '100%', background: 'rgba(255,255,255,0.05)', border: 'none', borderRadius: '8px', padding: '10px 12px' }}
            />
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              style={{ width: '100%', background: 'rgba(255,255,255,0.05)', border: 'none', borderRadius: '8px', padding: '10px 12px', color: 'inherit' }}
            >
              <option value="">🏷️ Tüm Etiketler</option>
              {allCategories.map(c => (
                <option key={c.slug} value={c.slug}>{c.label}</option>
              ))}
            </select>
          </div>

          <div
            style={{ flex: 1, overflowY: 'auto' }}
            className="custom-scrollbar"
            onScroll={(e) => {
              const el = e.currentTarget;
              if (contactsHasMore && !contactsLoadingMore && el.scrollHeight - el.scrollTop - el.clientHeight < 100) {
                fetchConversationsMore();
              }
            }}
          >
            {filteredContacts.length === 0 ? (
              <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                {(contactSearch.trim() || categoryFilter) ? 'Aramanızla eşleşen kişi bulunamadı.' : 'Gösterilecek mesaj bulunamadı.'}
              </div>
            ) : (
              filteredContacts.map(contact => (
                <div 
                  key={contact.id}
                  onClick={() => { setSelectedContactId(contact.id); if (isMobile) setMobileView('chat'); }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '16px',
                    gap: '12px',
                    cursor: 'pointer',
                    background: selectedContactId === contact.id ? 'rgba(99,102,241,0.1)' : 'transparent',
                    borderLeft: selectedContactId === contact.id ? '3px solid #6366f1' : '3px solid transparent',
                    borderBottom: '1px solid rgba(255,255,255,0.02)',
                    transition: 'all 0.2s'
                  }}
                  className="contact-item"
                >
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <div style={{
                      width: '48px', height: '48px', borderRadius: '50%',
                      background: 'linear-gradient(135deg, #6366f1, #ec4899)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 700, fontSize: '18px', color: '#fff'
                    }}>
                      {contact.hasRealName ? contact.name[0]?.toUpperCase() : (contact.platform === 'whatsapp' ? '💬' : '👤')}
                    </div>
                    <div title={platformInfo(contact.platform).label} style={{
                      position: 'absolute', bottom: '-2px', right: '-2px',
                      width: '20px', height: '20px', borderRadius: '50%',
                      background: platformInfo(contact.platform).color,
                      border: '2px solid #0d121e',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '10px'
                    }}>
                      {platformInfo(contact.platform).icon}
                    </div>
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    {(contact.tags.length > 0 || contact.accountLabel) && (
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '4px' }}>
                        {contact.accountLabel && (
                          <span title="Bağlı hesap" style={{
                            fontSize: '10px', fontWeight: 600, color: '#60a5fa',
                            background: 'rgba(96,165,250,0.15)', borderRadius: '999px',
                            padding: '1px 8px', whiteSpace: 'nowrap'
                          }}>
                            {contact.accountLabel}
                          </span>
                        )}
                        {contact.tags.map(tag => (
                          <span key={tag} style={{
                            fontSize: '10px', fontWeight: 600, color: '#a855f7',
                            background: 'rgba(168,85,247,0.15)', borderRadius: '999px',
                            padding: '1px 8px', whiteSpace: 'nowrap'
                          }}>
                            🏷️ {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
                      <h4 style={{ margin: 0, minWidth: 0, fontSize: '15px', color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {contact.name}
                      </h4>
                      <span
                        title={format(new Date(contact.lastMessage.created_at), 'd MMMM yyyy HH:mm', { locale: tr })}
                        style={{ fontSize: '11px', color: 'var(--text-muted)', whiteSpace: 'nowrap', flexShrink: 0, marginLeft: '8px' }}
                      >
                        {formatListTimestamp(contact.lastMessage.created_at)}
                      </span>
                    </div>
                    {contact.hasRealName && contact.displayPhone && (
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {contact.displayPhone}
                      </div>
                    )}
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <p style={{ 
                        margin: 0, fontSize: '13px', color: 'var(--text-muted)',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        paddingRight: '8px'
                      }}>
                        {contact.lastMessage.direction === 'outgoing' ? <span style={{ color: 'var(--tertiary)' }}>✓ </span> : ''}
                        {contact.lastMessage.content}
                      </p>
                      {contact.unreadCount > 0 && (
                        <div style={{ 
                          background: '#ef4444', color: '#fff', fontSize: '10px', 
                          fontWeight: 700, borderRadius: '10px', padding: '2px 6px',
                          flexShrink: 0
                        }}>
                          {contact.unreadCount}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
            {contactsHasMore && (
              <div style={{ padding: '14px', textAlign: 'center' }}>
                <button
                  onClick={fetchConversationsMore}
                  disabled={contactsLoadingMore}
                  style={{
                    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '8px', padding: '8px 16px', color: 'var(--text-muted)',
                    fontSize: '13px', fontWeight: 600, cursor: contactsLoadingMore ? 'default' : 'pointer',
                    opacity: contactsLoadingMore ? 0.6 : 1
                  }}
                >
                  {contactsLoadingMore ? '⏳ Yükleniyor...' : '↓ Daha Fazla Yükle'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Right Sidebar - Chat Window */}
        <div data-tour="msg-chat-pane" style={{ flex: 1, display: isMobile && mobileView === 'list' ? 'none' : 'flex', flexDirection: 'column', background: 'rgba(255,255,255,0.01)' }}>
          {selectedContact ? (
            <>
              {/* Ön Görüşme İşlemleri (Ayarlar > Genel'den açılıp kapatılabilir) - TÜM WhatsApp
                  sohbetlerinde görünür. Sohbete karşılık gelen bir ön görüşme talebi henüz
                  yoksa açıkça belirtilir; bu durumda butonlardan birine basmak (Sil hariç
                  hepsi) o talebi otomatik oluşturup ilgili duruma geçirir (bkz. ensureLinkedPrecons). */}
              {preconsToggleOn && selectedContact.platform === 'whatsapp' && (
                <div style={{ padding: '12px 24px', borderBottom: '1px solid rgba(255,255,255,0.05)', background: 'rgba(249, 115, 22, 0.04)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 600 }}>🔍 Ön Görüşme:</span>
                      {linkedPrecons ? getStatusBadgeInline(linkedPrecons.status) : (
                        <span style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--text-muted)', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600 }}>
                          ⚪ Henüz Ön Görüşme'de Değil
                        </span>
                      )}
                      {linkedPrecons?.status === 'callback_later' && linkedPrecons.callback_date && !showMsgCallbackPicker && (
                        <span style={{ fontSize: '11px', color: '#fb923c' }}>
                          🔁 {new Date(linkedPrecons.callback_date).toLocaleString('tr-TR', { day: '2-digit', month: 'long', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <button className="btn btn-sm btn-warning" onClick={() => updateLinkedPreconsStatus('contacted')}>📞 Arandı</button>
                      <button className="btn btn-sm btn-secondary" onClick={() => updateLinkedPreconsStatus('no_response')}>🔕 Cevap Yok</button>
                      <button
                        className="btn btn-sm"
                        style={{ background: 'rgba(249, 115, 22, 0.15)', color: '#fb923c', border: '1px solid rgba(249, 115, 22, 0.3)' }}
                        onClick={() => setShowMsgCallbackPicker(v => !v)}
                      >
                        🔁 Tekrar Ara
                      </button>
                      <button className="btn btn-sm btn-success" onClick={goToCalendarFromMessages} disabled={schedulingFromMsg}>
                        {schedulingFromMsg ? '⏳...' : '📅 Randevulandı'}
                      </button>
                      <button className="btn btn-sm btn-primary" onClick={() => updateLinkedPreconsStatus('completed')}>✓ Tamamlandı</button>
                      <button className="btn btn-sm btn-danger" onClick={() => updateLinkedPreconsStatus('lost')}>✕ Kayıp</button>
                    </div>
                  </div>
                  {showMsgCallbackPicker && (
                    <div style={{ marginTop: '10px', padding: '10px', borderRadius: '8px', background: 'rgba(249, 115, 22, 0.08)', border: '1px solid rgba(249, 115, 22, 0.25)', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '12px', color: '#fb923c', fontWeight: 600 }}>Hangi gün/saat tekrar aransın?</span>
                      <input
                        type="datetime-local"
                        className="input"
                        style={{ width: 'auto' }}
                        value={msgCallbackDateInput}
                        min={toDatetimeLocalValue(new Date())}
                        onChange={(e) => setMsgCallbackDateInput(e.target.value)}
                      />
                      <button className="btn btn-sm btn-warning" onClick={saveMsgCallbackLater} disabled={savingMsgCallback || !msgCallbackDateInput}>
                        {savingMsgCallback ? '⏳ Kaydediliyor...' : '✓ Onayla'}
                      </button>
                      <button className="btn btn-sm btn-secondary" onClick={() => setShowMsgCallbackPicker(false)}>İptal</button>
                    </div>
                  )}
                </div>
              )}

              {/* Chat Header */}
              <div style={{ 
                padding: isMobile ? '12px 16px' : '16px 24px', 
                borderBottom: '1px solid rgba(255,255,255,0.05)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: 'rgba(0,0,0,0.1)'
              }}>
                {/* Mobil geri butonu */}
                {isMobile && (
                  <button
                    onClick={() => { setMobileView('list'); setSelectedContactId(null); }}
                    style={{
                      background: 'none', border: 'none', color: 'var(--primary)',
                      cursor: 'pointer', padding: '4px 8px 4px 0', display: 'flex',
                      alignItems: 'center', gap: '4px', fontSize: '14px', fontWeight: 600,
                      marginRight: '4px', flexShrink: 0
                    }}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="20" height="20">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 18l-6-6 6-6"/>
                    </svg>
                  </button>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1, minWidth: 0 }}>
                  <div style={{ 
                    width: '42px', height: '42px', borderRadius: '50%', 
                    background: 'linear-gradient(135deg, #6366f1, #ec4899)', 
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: '16px', color: '#fff'
                  }}>
                    {selectedContact.hasRealName ? selectedContact.name[0]?.toUpperCase() : (selectedContact.platform === 'whatsapp' ? '💬' : '👤')}
                  </div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '16px', color: '#fff' }}>{selectedContact.name}</h3>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: platformInfo(selectedContact.platform).color, fontWeight: 600, textTransform: 'uppercase' }}>
                        {platformInfo(selectedContact.platform).icon} {platformInfo(selectedContact.platform).label}
                      </span>
                      {selectedContact.accountLabel && (
                        <span title="Bağlı hesap" style={{
                          fontSize: '10px', fontWeight: 600, color: '#60a5fa',
                          background: 'rgba(96,165,250,0.15)', borderRadius: '999px', padding: '1px 8px'
                        }}>
                          {selectedContact.accountLabel}
                        </span>
                      )}
                      {selectedContact.displayPhone && (
                        <span style={{ color: '#fff', fontWeight: 600 }}>• {selectedContact.displayPhone}</span>
                      )}
                    </div>
                    {selectedContact.tags.length > 0 && (
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '6px' }}>
                        {selectedContact.tags.map(tag => (
                          <span key={tag} style={{
                            fontSize: '11px', fontWeight: 600, color: '#a855f7',
                            background: 'rgba(168,85,247,0.15)', borderRadius: '999px',
                            padding: '2px 10px', whiteSpace: 'nowrap'
                          }}>
                            🏷️ {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                
                {selectedContact.phone && (
                  <div data-tour="msg-mode-toggle" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button
                      onClick={() => handleToggleMode(selectedContact.phone, 'ai', selectedContact.platform)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: '1px solid',
                        borderColor: selectedContact.handoverMode === 'ai' ? '#10b981' : 'rgba(255,255,255,0.1)',
                        background: selectedContact.handoverMode === 'ai' ? 'rgba(16,185,129,0.15)' : 'transparent',
                        color: selectedContact.handoverMode === 'ai' ? '#10b981' : 'var(--text-muted)',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'all 0.2s'
                      }}
                    >
                      🤖 AI Asistanı Aktif
                    </button>
                    <button
                      onClick={() => handleToggleMode(selectedContact.phone, 'human', selectedContact.platform)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: '1px solid',
                        borderColor: selectedContact.handoverMode === 'human' ? '#ef4444' : 'rgba(255,255,255,0.1)',
                        background: selectedContact.handoverMode === 'human' ? 'rgba(239,68,68,0.15)' : 'transparent',
                        color: selectedContact.handoverMode === 'human' ? '#ef4444' : 'var(--text-muted)',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'all 0.2s'
                      }}
                    >
                      👤 Personele Devret
                    </button>
                    {selectedContact.platform === 'whatsapp' && (
                      <button
                        onClick={handleCreateAppointment}
                        disabled={creatingAppointment}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '6px',
                          border: '1px solid rgba(255,255,255,0.1)',
                          background: 'transparent',
                          color: 'var(--text-muted)',
                          fontSize: '13px',
                          fontWeight: 600,
                          cursor: creatingAppointment ? 'default' : 'pointer',
                          opacity: creatingAppointment ? 0.6 : 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          transition: 'all 0.2s'
                        }}
                      >
                        📅 {creatingAppointment ? 'Açılıyor...' : 'Randevu Oluştur'}
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Chat Messages */}
              <div
                ref={messagesScrollRef}
                onScroll={handleThreadScroll}
                style={{
                  flex: 1,
                  padding: isMobile ? '12px' : '24px',
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }} className="custom-scrollbar">
                {threadLoading && (
                  <div style={{ textAlign: 'center', padding: '12px', color: 'var(--text-muted)', fontSize: '13px' }}>Yükleniyor...</div>
                )}
                {!threadLoading && threadLoadingOlder && (
                  <div style={{ textAlign: 'center', padding: '8px', color: 'var(--text-muted)', fontSize: '12px' }}>Daha eski mesajlar yükleniyor...</div>
                )}
                {!threadLoading && !threadLoadingOlder && !threadHasMore && selectedContact.messages.length > 0 && (
                  <div style={{ textAlign: 'center', padding: '8px', color: 'var(--text-muted)', fontSize: '11.5px', opacity: 0.6 }}>Sohbetin başlangıcı</div>
                )}
                {selectedContact.messages.map((msg, idx) => {
                  const isOutgoing = msg.direction === 'outgoing';
                  const prevMsg = selectedContact.messages[idx - 1];
                  const showDayDivider = !prevMsg || !isSameDay(prevMsg.created_at, msg.created_at);

                  return (
                    <React.Fragment key={msg.id}>
                    {showDayDivider && (
                      <div style={{ display: 'flex', justifyContent: 'center', margin: '8px 0' }}>
                        <span style={{
                          fontSize: '11.5px', color: 'var(--text-muted)', background: 'rgba(255,255,255,0.06)',
                          border: '1px solid rgba(255,255,255,0.08)', borderRadius: '999px', padding: '3px 12px'
                        }}>
                          {formatDayDivider(msg.created_at)}
                        </span>
                      </div>
                    )}
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: isOutgoing ? 'flex-end' : 'flex-start',
                      width: '100%'
                    }}>
                      <div style={{
                        maxWidth: '75%',
                        padding: '12px 16px',
                        borderRadius: '16px',
                        borderTopRightRadius: isOutgoing ? '4px' : '16px',
                        borderTopLeftRadius: !isOutgoing ? '4px' : '16px',
                        background: isOutgoing ? '#6366f1' : 'rgba(255,255,255,0.08)',
                        color: '#fff',
                        fontSize: '14.5px',
                        lineHeight: '1.5',
                        position: 'relative',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }}>
                        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {msg.media_type === 'image' && msg.media_url ? (
                            <a href={msg.media_url} target="_blank" rel="noopener noreferrer">
                              <img src={msg.media_url} alt={msg.content || 'Fotoğraf'} style={{ maxWidth: '260px', maxHeight: '260px', borderRadius: '10px', display: 'block' }} />
                            </a>
                          ) : msg.media_type === 'video' && msg.media_url ? (
                            <video controls src={msg.media_url} style={{ maxWidth: '260px', maxHeight: '260px', borderRadius: '10px', display: 'block' }} />
                          ) : msg.media_type === 'audio' && msg.media_url ? (
                            <audio controls src={msg.media_url} style={{ maxWidth: '260px' }} />
                          ) : msg.media_type === 'document' && msg.media_url ? (
                            <a href={msg.media_url} target="_blank" rel="noopener noreferrer" style={{ color: '#fff', textDecoration: 'underline' }}>
                              📎 {msg.content || 'Dosyayı indir'}
                            </a>
                          ) : (
                            msg.content || (msg.platform === 'whatsapp' ? 'Medya veya sesli mesaj' : 'İçerik yok')
                          )}
                        </div>
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'flex-end', 
                          alignItems: 'center', 
                          gap: '4px',
                          marginTop: '4px', 
                          fontSize: '10.5px', 
                          color: isOutgoing ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.5)'
                        }} title={format(new Date(msg.created_at), 'd MMMM yyyy HH:mm', { locale: tr })}>
                          {format(new Date(msg.created_at), 'HH:mm')}
                          {isOutgoing && (
                            <span>✓</span>
                          )}
                        </div>
                      </div>
                    </div>
                    </React.Fragment>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Chat Input */}
              <form onSubmit={handleSendMessage} style={{ 
                padding: isMobile ? '10px 12px' : '16px 24px', 
                borderTop: '1px solid rgba(255,255,255,0.05)',
                background: 'rgba(0,0,0,0.1)',
                display: 'flex',
                gap: '12px',
                width: '100%'
              }}>
                <input 
                  type="text" 
                  value={typedMessage}
                  onChange={(e) => setTypedMessage(e.target.value)}
                  placeholder={!selectedContact.phone ? "Bu sohbette numara kayıtlı değil, cevap yazılamaz." : (sending ? "Gönderiliyor..." : "Mesajınızı yazın...")}
                  disabled={sending || !selectedContact.phone}
                  style={{ 
                    flex: 1, 
                    background: 'rgba(255,255,255,0.05)', 
                    border: '1px solid rgba(255,255,255,0.1)', 
                    borderRadius: '24px', 
                    padding: '12px 20px',
                    color: 'white',
                    outline: 'none',
                    opacity: (!selectedContact.phone) ? 0.5 : 1
                  }}
                />
                <button
                  type="submit"
                  disabled={!typedMessage.trim() || sending || !selectedContact.phone}
                  style={{
                    padding: '0 24px',
                    borderRadius: '24px',
                    background: '#6366f1',
                    color: '#fff',
                    border: 'none',
                    fontWeight: 600,
                    cursor: 'pointer',
                    opacity: (!typedMessage.trim() || sending || !selectedContact.phone) ? 0.5 : 1,
                    transition: 'all 0.2s'
                  }}
                >
                  Gönder
                </button>
              </form>
            </>
          ) : (
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
              <div style={{ 
                width: '80px', height: '80px', borderRadius: '50%', 
                background: 'rgba(255,255,255,0.02)', 
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: '16px'
              }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h3>Mesajlara Başlayın</h3>
              <p style={{ marginTop: '4px' }}>Detayları görmek için sol taraftan bir sohbete tıklayın.</p>
            </div>
          )}
        </div>
      </div>
      
      <style>{`
        .contact-item:hover {
          background: rgba(255,255,255,0.02) !important;
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.1);
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255,255,255,0.2);
        }
        .msg-switch { position: relative; display: inline-block; width: 40px; height: 22px; flex-shrink: 0; }
        .msg-switch input { opacity: 0; width: 0; height: 0; }
        .msg-switch-slider { position: absolute; cursor: pointer; inset: 0; background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.1); border-radius: 22px; transition: 0.25s; }
        .msg-switch-slider:before { position: absolute; content: ""; height: 16px; width: 16px; left: 2px; bottom: 2px; background: #fff; border-radius: 50%; transition: 0.25s; box-shadow: 0 2px 4px rgba(0,0,0,0.3); }
        .msg-switch input:checked + .msg-switch-slider { background: rgba(251,191,36,0.25); border-color: rgba(251,191,36,0.5); }
        .msg-switch input:checked + .msg-switch-slider:before { transform: translateX(18px); background: #FBBF24; }
      `}</style>
    </div>
  );
}

export default Messages;
